/**
 * SASADM-0105 / SASADM-0139 / SASADM-0148 — the product-admin panel: the product-scoped
 * console app, its derived console hostnames, plus the operators appointed to administer
 * this product's tenants (`product_admins`). The people controls use
 * `/api/saas-admin/products/{productId}/admins`; the console app reuses ProductApps' app
 * API and editor; the hostnames table reads `/api/saas-admin/products/{productId}/hostnames`
 * (saas-auth SASATH-0216 + SASATH-0330) — derived only, no add/delete/primary.
 * SASADM-0152 — the table is the shared ConsoleHostnamesTable, filtered to this
 * console's `appId`, so a reseller console's rows never show here.
 *
 * The people section mirrors the platform OperatorsPanel (list table + grant dialog + revoke
 * ConfirmDialog + useSnackbar), scoped to one product. Unlike that screen this is a SECTION on
 * the product detail page, so it self-fetches after mount like ProductApps / ProductInternalKeys
 * rather than using a route loader — a SAAS_ADMIN viewer's page is never broken by a 403 here.
 *
 * SUPER_ADMIN-only and double-guarded: ProductDetail gates the mount on
 * `operator?.isSuperAdmin`, and this component returns null (and never fetches)
 * for a non-super principal so a stale render can't expose the appointment
 * controls.
 *
 * Semantics (all mirror operators): a grant targets an EXISTING user and is
 * idempotent; a revoke is soft and immediate, and a 404 means already-revoked, so
 * it is treated as success. A product may legitimately have ZERO admins — there
 * is no "last admin" guard.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { useCurrentOperator } from '@/auth/AuthContext';
import type {
  AppTopology,
  ProductAdminDto,
  ProductAppDto,
  ProductHostnameDto,
  RoutingDeployOutcome,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConsoleHostnamesTable } from '@/components/ConsoleHostnamesTable';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';
import { AppDialog } from './ProductApps';

export function ProductAdmins({
  productId,
  productSlug,
  appTopology,
}: {
  productId: number;
  productSlug: string;
  appTopology: AppTopology;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const isSuperAdmin = operator?.isSuperAdmin === true;
  const [rows, setRows] = useState<ProductAdminDto[] | null>(null);
  const [apps, setApps] = useState<ProductAppDto[] | null>(null);
  const [hostnames, setHostnames] = useState<ProductHostnameDto[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [peopleLoadFailed, setPeopleLoadFailed] = useState(false);
  const [appsLoadFailed, setAppsLoadFailed] = useState(false);
  const [hostnamesLoadFailed, setHostnamesLoadFailed] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [openGrant, setOpenGrant] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ProductAdminDto | null>(null);
  const [revoking, setRevoking] = useState(false);
  // SASADM-0172 (0170 leftover) — a console save whose routing deploy was REJECTED / FAILED.
  const [deployFailure, setDeployFailure] = useState<RoutingDeployOutcome | null>(null);

  async function refreshPeople() {
    // Cleared up front so a later success drops the forbidden state.
    setForbidden(false);
    setPeopleLoadFailed(false);
    setRows(null);
    try {
      setRows(await apiAdmin.productAdmins.list(productId, includeRevoked));
    } catch (err) {
      // SASADM-0109 — a live 403 (the client believes the principal is super,
      // the backend disagrees) is a permissions state, not an admin-less
      // product; show what the sibling super-admin sections show rather than
      // "No product admins yet." Keyed on the HTTP status: `mapBackendCode`
      // collapses an unrecognised code to `UNKNOWN`, so a code check would
      // miss a plain 403.
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        setRows([]);
        return;
      }
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      else snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      setRows(null);
      setPeopleLoadFailed(true);
    }
  }

  async function refreshApps() {
    setAppsLoadFailed(false);
    setApps(null);
    try {
      setApps(await apiAdmin.productApps.list(productId));
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      else snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      // Unknown must stay distinct from a confirmed empty list. In particular,
      // never expose Add-console after a failed read: the singleton may exist.
      setApps(null);
      setAppsLoadFailed(true);
    }
  }

  useEffect(() => {
    // Never hit the SUPER_ADMIN-only endpoint for a non-super principal.
    if (!isSuperAdmin) return;
    void (async () => {
      await refreshPeople();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, includeRevoked, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    void (async () => {
      await refreshApps();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isSuperAdmin]);

  async function refreshHostnames() {
    setHostnamesLoadFailed(false);
    setHostnames(null);
    try {
      setHostnames(await apiAdmin.productHostnames.list(productId));
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      else snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      setHostnames(null);
      setHostnamesLoadFailed(true);
    }
  }

  useEffect(() => {
    if (!isSuperAdmin) return;
    void (async () => {
      await refreshHostnames();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isSuperAdmin]);

  async function revoke(target: ProductAdminDto) {
    setRevoking(true);
    try {
      await apiAdmin.productAdmins.revoke(productId, target.userId);
      snack.success(t('productAdmins.revoke.success', 'Product admin access revoked.'));
      await refreshPeople();
    } catch (err) {
      // A 404 means the grant was already revoked — the end state the operator
      // asked for, so treat it as success and refresh. Checked ahead of the 403
      // branch so the already-revoked reading stays intact.
      if (err instanceof ApiError && err.status === 404) {
        snack.success(t('productAdmins.revoke.success', 'Product admin access revoked.'));
        await refreshPeople();
      } else if (err instanceof ApiError && err.status === 403) {
        // SASADM-0113 — parity with ProductInternalKeys' mutation paths: a 403
        // on a mutation is the same permissions state as a 403 on the list, so
        // the section flips to the guarded render rather than only flashing a
        // snackbar over controls that cannot work. `refreshPeople()` clears it again
        // on a later success.
        setForbidden(true);
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      }
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
    }
  }

  // Belt-and-suspenders: the mount in ProductDetail is already gated.
  if (!isSuperAdmin) return null;

  const productAdminApp = apps?.find((app) => app.role === 'PRODUCT_ADMIN') ?? null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {t('productAdmins.title', 'Product admins')}
      </Typography>
      <Stack spacing={3}>
        {deployFailure && (
          <RoutingDeployBanner
            outcome={deployFailure}
            onClose={() => setDeployFailure(null)}
            action={
              <Link component={RouterLink} to="/operations" color="inherit">
                {t('routingDeploy.openOperations', 'Open Operations to deploy routing')}
              </Link>
            }
          />
        )}
        {appsLoadFailed ? (
          <Alert severity="error">
            {t(
              'products.productAdmins.consoleLoadError',
              "Product-admin console details couldn't be loaded. Its existence is unknown, so adding a console is unavailable.",
            )}
          </Alert>
        ) : apps === null ? (
          <Typography color="text.secondary">{t('common.loading', 'Loading')}</Typography>
        ) : productAdminApp === null ? (
          <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" color="text.secondary">
              {appTopology === 'SELF_CONTAINED'
                ? t(
                    'products.productAdmins.noConsoleSelfContained',
                    "This product is self-contained, so it can't have a separate product-admin console. Change its packaging before adding one.",
                  )
                : t(
                    'products.productAdmins.noConsole',
                    'This product has no product-admin console. Add one to give staff a single URL for administering this product across every tenant.',
                  )}
            </Typography>
            {appTopology === 'COMPOSED' ? (
              <Button startIcon={<AddIcon />} onClick={() => setAppDialogOpen(true)}>
                {t('products.productAdmins.addConsole', 'Add product admin console')}
              </Button>
            ) : null}
          </Stack>
        ) : (
          <Box component="section" aria-labelledby="product-admin-app-section">
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}
            >
              <Typography id="product-admin-app-section" variant="h4">
                {t('products.productAdmins.appSection', 'Product admin console')}
              </Typography>
              <Button size="small" startIcon={<EditIcon />} onClick={() => setAppDialogOpen(true)}>
                {t('common.edit', 'Edit')}
              </Button>
            </Stack>
            <Stack spacing={0.5} sx={{ mt: 1.5 }}>
              <Typography component="p" variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                {productAdminApp.slug}
              </Typography>
              <Typography variant="body2">{productAdminApp.displayName}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {productAdminApp.docroot ?? '—'}
              </Typography>
            </Stack>
            <Box sx={{ mt: 2 }}>
              <ConsoleHostnamesTable
                productId={productId}
                appId={productAdminApp.id}
                hostnames={hostnames}
                loadFailed={hostnamesLoadFailed}
                isPlatformRow={(row) => row.isPrimary}
                explainer={t(
                  'products.productAdmins.hostnames.explainer',
                  'Console hostnames are derived from the product slug, the platform base and (when set) the product base domain. Point *.<base domain> at the platform host, Check DNS on each row, then Deploy Platform Routing.',
                )}
                onRowVerified={(verified) =>
                  setHostnames((prev) =>
                    prev ? prev.map((r) => (r.id === verified.id ? verified : r)) : prev,
                  )
                }
              />
            </Box>
          </Box>
        )}

        {productAdminApp !== null || appsLoadFailed ? (
          <Box component="section" aria-labelledby="product-admin-people-section">
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography id="product-admin-people-section" variant="h4">
                {t('products.productAdmins.peopleSection', 'People')}
              </Typography>
              {!forbidden && !peopleLoadFailed && rows !== null ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={includeRevoked}
                        onChange={(e) => setIncludeRevoked(e.target.checked)}
                      />
                    }
                    label={t('productAdmins.includeRevoked', 'Show revoked')}
                  />
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setOpenGrant(true)}>
                    {t('productAdmins.grant.action', 'Grant product admin')}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                'productAdmins.explanation',
                "Product admins administer this product's tenants. Appointing someone requires an existing user — they must sign in once first.",
              )}
            </Typography>
            {forbidden ? (
              <Alert severity="warning">
                {t(
                  'productAdmins.superAdminRequired',
                  'Super admin required to manage product admins.',
                )}
              </Alert>
            ) : peopleLoadFailed ? (
              <Alert severity="error">
                {t(
                  'products.productAdmins.peopleLoadError',
                  "Product admins couldn't be loaded. Console app details remain available.",
                )}
              </Alert>
            ) : rows === null ? (
              <Typography color="text.secondary">{t('common.loading', 'Loading')}</Typography>
            ) : rows.length === 0 ? (
              <Typography color="text.secondary">
                {t('productAdmins.empty', 'No product admins yet.')}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('productAdmins.columns.email', 'Email')}</TableCell>
                      <TableCell>{t('productAdmins.columns.role', 'Role')}</TableCell>
                      <TableCell>{t('productAdmins.columns.granted', 'Granted')}</TableCell>
                      <TableCell>{t('productAdmins.columns.grantedBy', 'Granted by')}</TableCell>
                      <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={t(`productAdmins.role.${row.role}`, 'Product admin')}
                          />
                        </TableCell>
                        <TableCell>{new Date(row.grantedAt).toLocaleDateString()}</TableCell>
                        {/* SASATH-0213 resolves the granter's email; until it deploys
                            the backend sends only the raw id, so that is what renders
                            today — prefixed `#` to read as an id rather than a count. */}
                        <TableCell>
                          {row.grantedByEmail ??
                            (row.grantedBy != null ? `#${row.grantedBy}` : '—')}
                        </TableCell>
                        <TableCell align="right">
                          {/* A revoked row is only visible under "Show revoked"; it has
                              nothing left to revoke, so it carries a muted chip. */}
                          {row.revokedAt != null ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('productAdmins.revoked', 'Revoked')}
                            />
                          ) : (
                            <Button size="small" color="error" onClick={() => setRevokeTarget(row)}>
                              {t('productAdmins.actions.revoke', 'Revoke')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : null}
      </Stack>
      {appDialogOpen ? (
        <AppDialog
          open
          productId={productId}
          productSlug={productSlug}
          appRole="PRODUCT_ADMIN"
          app={productAdminApp}
          knownApps={apps ?? []}
          tenantAdminExists={false}
          onBlockedRoleChange={() => undefined}
          onClose={() => setAppDialogOpen(false)}
          onSaved={() => {
            setAppDialogOpen(false);
            void refreshApps();
          }}
          onDeployFailure={setDeployFailure}
        />
      ) : null}
      <GrantProductAdminDialog
        open={openGrant}
        productId={productId}
        onClose={() => setOpenGrant(false)}
        onForbidden={() => {
          setOpenGrant(false);
          setForbidden(true);
        }}
        onGranted={(email) => {
          setOpenGrant(false);
          void refreshPeople();
          snack.success(
            t('productAdmins.grant.success', 'Product admin granted to {{email}}.', { email }),
          );
        }}
      />
      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={t('productAdmins.revoke.title', 'Revoke product admin?')}
        body={t(
          'productAdmins.revoke.body',
          'Remove {{email}} as a product admin. They lose access to this product on their next request.',
          { email: revokeTarget?.email ?? '' },
        )}
        severity="error"
        confirmLabel={t('productAdmins.actions.revoke', 'Revoke')}
        busy={revoking}
        onConfirm={() => {
          if (revokeTarget) void revoke(revokeTarget);
        }}
        onCancel={() => setRevokeTarget(null)}
      />
    </Paper>
  );
}

interface GrantProductAdminDialogProps {
  open: boolean;
  productId: number;
  onClose: () => void;
  /** SASADM-0113 — a grant 403 is the section's permissions state, not the
      dialog's, so it is handed up (mirrors IssueKeyDialog). */
  onForbidden: () => void;
  onGranted: (email: string) => void;
}

/**
 * Appoint an EXISTING user as a product admin. There is exactly one role today
 * (`PRODUCT_ADMIN`), so the dialog carries no role selector; the grant is
 * idempotent, so re-granting an active admin succeeds.
 */
function GrantProductAdminDialog({
  open,
  productId,
  onClose,
  onForbidden,
  onGranted,
}: GrantProductAdminDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset the form on the open transition (render-phase state adjustment — no
  // effect needed).
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setEmail('');
      setEmailError(null);
      setPending(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    setPending(true);
    setEmailError(null);
    try {
      await apiAdmin.productAdmins.grant(productId, { email: trimmed, role: 'PRODUCT_ADMIN' });
      onGranted(trimmed);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // SASADM-0113 — the backend refuses the appointment: a permissions
        // state for the whole section, so close and let it show the guarded
        // render instead of an inline field error.
        onForbidden();
        return;
      }
      if (err instanceof ApiError && (err.status === 404 || err.code === 'USER_NOT_FOUND')) {
        // The panel appoints; it does not create users.
        setEmailError(
          t(
            'productAdmins.grant.noUser',
            'No user with that email — they must sign in once before they can be appointed.',
          ),
        );
      } else if (err instanceof ApiError && err.status === 400) {
        // Defensive: the backend rejects a role change on an active grant. Only
        // one role exists today, so this is unreachable from this dialog.
        snack.error(t('productAdmins.grant.roleChange', 'Revoke then re-grant to change role.'));
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('productAdmins.grant.title', 'Grant product admin')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={submit} id="grant-product-admin-form" noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="email"
              required
              label={t('productAdmins.grant.email', 'User email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(emailError)}
              helperText={emailError ?? undefined}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          form="grant-product-admin-form"
          type="submit"
          variant="contained"
          disabled={pending}
        >
          {t('productAdmins.grant.submit', 'Grant')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
